/**
 * P3 Hold/Continue SoT Soft≠ invent — Team Runtime control plane.
 * Hold: Team only Soft≠ invent AI Incident approve=false.
 * Continue: Team Runtime only. No AI Incident approve fallback (fail-closed).
 * Audience: tenant CMC admin+ (same floor as Team control).
 */
import { continueRefuseReasonFromInspect } from "./autonomy-planes.js";

export { continueRefuseToast, holdRefuseToast } from "./autonomy-planes.js";

async function refuseReasonWhenNoRun(apiFn) {
  if (typeof apiFn !== "function") return "needs_team_run";
  try {
    const eff = await apiFn("/ai/autonomy/policy/effective", { method: "GET" });
    return continueRefuseReasonFromInspect(eff);
  } catch (_) {
    return "needs_team_run";
  }
}

/** Resolve team_run_id + control_generation for an incident via Live GET. */
export async function resolveTeamRunForIncident(apiFn, incidentId) {
  const id = String(incidentId || "").trim();
  if (!id || typeof apiFn !== "function") return { team_run_id: "", control_generation: 0 };
  try {
    const pack = await apiFn(`/ai/team/incidents/${encodeURIComponent(id)}/run`, {
      method: "GET",
    });
    const rid = String((pack && pack.run && pack.run.team_run_id) || "").trim();
    const gen = Number(
      (pack && pack.run && pack.run.control_generation)
      || (pack && pack.mission && pack.mission.control_generation)
      || 0,
    );
    return { team_run_id: rid, control_generation: gen >= 1 ? gen : 0, pack };
  } catch (_) {
    return { team_run_id: "", control_generation: 0 };
  }
}

/**
 * Execute Hold on Team Runtime. Returns result or { ok:false, reason }.
 * Soft≠ invent: refuses when no TeamRun — caller must toast Mission Room.
 */
export async function holdTeamRunOrRefuse(apiFn, pathFn, incidentId, opts = {}) {
  const known = String(opts.team_run_id || "").trim();
  let rid = known;
  let gen = Number(opts.control_generation || 0);
  if (!rid) {
    const resolved = await resolveTeamRunForIncident(apiFn, incidentId);
    rid = resolved.team_run_id;
    gen = resolved.control_generation;
  }
  if (!rid) {
    const reason = await refuseReasonWhenNoRun(apiFn);
    return {
      ok: false,
      reason,
      soft_invent: false,
      live: false,
    };
  }
  const url =
    (typeof pathFn === "function"
      && pathFn("post__ai_team_runs_p_hold", { pathParams: [rid] }))
    || `/ai/team/runs/${encodeURIComponent(rid)}/hold`;
  const body = {
    control_generation: gen >= 1 ? gen : Number(opts.control_generation || 1) || 1,
    trigger: String(opts.trigger || "cmc_hold"),
  };
  try {
    const out = await apiFn(url, { method: "POST", body });
    if (!out) {
      return { ok: false, reason: "hold_refused", soft_invent: false, live: true };
    }
    return {
      ok: true,
      team_run_id: rid,
      hold_via: "team_runtime",
      soft_invent: false,
      live: true,
      out,
    };
  } catch (_) {
    return { ok: false, reason: "hold_error", soft_invent: false, live: true };
  }
}

/**
 * Prefer Team Runtime continue when TeamRun exists.
 * Returns { ok, continue_via, reason? }.
 */
export async function continueTeamRunPrefer(apiFn, pathFn, incidentId, opts = {}) {
  const known = String(opts.team_run_id || "").trim();
  let rid = known;
  let gen = Number(opts.control_generation || 0);
  if (!rid) {
    const resolved = await resolveTeamRunForIncident(apiFn, incidentId);
    rid = resolved.team_run_id;
    gen = resolved.control_generation;
  }
    if (!rid) {
      const reason = await refuseReasonWhenNoRun(apiFn);
      return {
        ok: false,
        reason,
        continue_via: reason,
        soft_invent: false,
        live: false,
      };
    }
  const url =
    (typeof pathFn === "function"
      && pathFn("post__ai_team_runs_p_continue", { pathParams: [rid] }))
    || `/ai/team/runs/${encodeURIComponent(rid)}/continue`;
  const body = {
    control_generation: gen >= 1 ? gen : Number(opts.control_generation || 1) || 1,
    trigger: String(opts.trigger || "cmc_continue"),
  };
  try {
    const out = await apiFn(url, { method: "POST", body });
    if (!out) {
      return { ok: false, reason: "continue_refused", soft_invent: false, live: true };
    }
    return {
      ok: true,
      team_run_id: rid,
      continue_via: "team_runtime",
      soft_invent: false,
      live: true,
      out,
    };
  } catch (_) {
    return { ok: false, reason: "continue_error", soft_invent: false, live: true };
  }
}

/**
 * L6: leftover escalate → Team Takeover. Never POST Incident /escalate.
 */
export async function takeoverTeamRunPrefer(apiFn, pathFn, incidentId, opts = {}) {
  const known = String(opts.team_run_id || "").trim();
  let rid = known;
  let gen = Number(opts.control_generation || 0);
  if (!rid) {
    const resolved = await resolveTeamRunForIncident(apiFn, incidentId);
    rid = resolved.team_run_id;
    gen = resolved.control_generation;
  }
  if (!rid) {
    const reason = await refuseReasonWhenNoRun(apiFn);
    return {
      ok: false,
      reason,
      takeover_via: reason,
      soft_invent: false,
      live: false,
    };
  }
  const url =
    (typeof pathFn === "function"
      && pathFn("post__ai_team_runs_p_takeover", { pathParams: [rid] }))
    || `/ai/team/runs/${encodeURIComponent(rid)}/takeover`;
  const body = {
    control_generation: gen >= 1 ? gen : Number(opts.control_generation || 1) || 1,
    trigger: String(opts.trigger || "cmc_takeover"),
  };
  try {
    const out = await apiFn(url, { method: "POST", body });
    if (!out) {
      return { ok: false, reason: "takeover_refused", soft_invent: false, live: true };
    }
    return {
      ok: true,
      team_run_id: rid,
      takeover_via: "team_runtime",
      soft_invent: false,
      live: true,
      out,
    };
  } catch (_) {
    return { ok: false, reason: "takeover_error", soft_invent: false, live: true };
  }
}

/**
 * L6: leftover request-evidence → Team Intervene. Never POST Incident /request-evidence.
 */
export async function interveneTeamRunPrefer(apiFn, pathFn, incidentId, opts = {}) {
  const known = String(opts.team_run_id || "").trim();
  let rid = known;
  let gen = Number(opts.control_generation || 0);
  if (!rid) {
    const resolved = await resolveTeamRunForIncident(apiFn, incidentId);
    rid = resolved.team_run_id;
    gen = resolved.control_generation;
  }
  if (!rid) {
    const reason = await refuseReasonWhenNoRun(apiFn);
    return {
      ok: false,
      reason,
      evidence_via: reason,
      soft_invent: false,
      live: false,
    };
  }
  const url =
    (typeof pathFn === "function"
      && pathFn("post__ai_team_runs_p_intervene", { pathParams: [rid] }))
    || `/ai/team/runs/${encodeURIComponent(rid)}/intervene`;
  const note = String(opts.note || "operator requested more evidence").trim() || "operator requested more evidence";
  const body = {
    control_generation: gen >= 1 ? gen : Number(opts.control_generation || 1) || 1,
    note,
    facts: Array.isArray(opts.facts) && opts.facts.length ? opts.facts.slice(0, 8) : ["evidence_requested"],
  };
  try {
    const out = await apiFn(url, { method: "POST", body });
    if (!out) {
      return { ok: false, reason: "intervene_refused", soft_invent: false, live: true };
    }
    return {
      ok: true,
      team_run_id: rid,
      evidence_via: "team_runtime",
      soft_invent: false,
      live: true,
      out,
    };
  } catch (_) {
    return { ok: false, reason: "intervene_error", soft_invent: false, live: true };
  }
}
